/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api';
import type { BreakdownLine, OperationBreakdown } from '../../api/areas/services';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { FactRows } from '../../components/flow';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useApi } from '../../lib/useApi';
import { useSearchParams } from '../../lib/scopedParams';
import { Amount, setParam } from '../commerce/shared';
import { selectClass } from '../operator/shared';
import { useEnum } from './shared';

const KIND_TONE: Record<BreakdownLine['kind'], Tone> = { SERVICE: 'info', REGISTRY_FEE: 'warn', REGISTRY_MAINTENANCE: 'warn', ADDON: 'pending' };

export function OperationQuote({ op }: { op: OperationBreakdown }) {
  const { t, i18n } = useTranslation('ux');
  const e = useEnum();
  const desc = (l: BreakdownLine) => (l.kind === 'SERVICE' ? e('lineKind', 'SERVICE') : i18n.language === 'vi' ? l.description_vi : l.description_en);
  const cols: Column<BreakdownLine>[] = [
    {
      key: 'item',
      header: t('quote.col.item'),
      cell: (l) => (
        <div className="space-y-0.5">
          <div className="font-medium">{desc(l)}</div>
          <div className="font-mono text-xs text-muted-foreground">{l.addon_code ?? l.code}</div>
        </div>
      ),
    },
    { key: 'kind', header: t('quote.col.kind'), cell: (l) => <Badge tone={KIND_TONE[l.kind]}>{e('lineKind', l.kind)}</Badge> },
    { key: 'amount', header: t('quote.col.amount'), numeric: true, cell: (l) => <Amount amount={l.amount} currency={op.currency} /> },
    { key: 'discount', header: t('quote.col.discount'), numeric: true, cell: (l) => (Number(l.discount) ? <Amount amount={l.discount} currency={op.currency} /> : '—') },
    { key: 'tax', header: t('quote.col.tax'), numeric: true, cell: (l) => (l.tax_category === 'KCT' ? 'KCT' : <Amount amount={l.tax} currency={op.currency} />) },
    { key: 'total', header: t('quote.col.total'), numeric: true, cell: (l) => <b><Amount amount={l.total} currency={op.currency} /></b> },
  ];
  if (op.error) {
    return (
      <Callout tone="warn" title={t('quote.cannot', { op: e('operation', op.operation) })}>
        {op.error.replace(/^no price available: /, '')}
      </Callout>
    );
  }
  return (
    <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px] [&>*]:min-w-0">
      <Section flush title={t('quote.lines', { op: e('operation', op.operation), years: op.years })} description={t('quote.linesHint')}>
        <DataTable columns={cols} rows={op.lines} rowKey={(l) => `${l.kind}-${l.code}-${l.addon_code ?? ''}`} empty={<EmptyState title={t('quote.noLines')} />} />
      </Section>
      <Section title={t('quote.totals')}>
        <FactRows
          rows={[
            op.registry_cost !== undefined && op.registry_cost !== null && [t('quote.registryCost'), <Amount key="c" amount={op.registry_cost} currency={op.currency} />],
            [t('quote.subtotal'), <Amount key="s" amount={op.subtotal} currency={op.currency} />],
            [t('quote.tax'), <Amount key="t" amount={op.tax} currency={op.currency} />],
            [t('quote.total'), <b key="g"><Amount amount={op.total} currency={op.currency} /></b>],
          ]}
        />
        <p className="mt-3 text-xs text-muted-foreground">{t('quote.costNote')}</p>
      </Section>
    </div>
  );
}

/**
 * `/products?tab=quote`, reference 26 "Báo giá · add-ons & phí": what one TLD's operation costs
 * the customer in a price book, line by line — service, pass-through fees and add-ons — from
 * the same pricing the checkout uses. Nothing is saved; a customer quote is made from the
 * registration dialog.
 */
export function QuotePage() {
  const { t } = useTranslation('ux');
  const e = useEnum();
  const tlds = useApi((signal) => api.listTlds({ limit: 500 }, { signal }), []);
  const pricing = useApi((signal) => api.getPricing({}, { signal }), []);
  const [params, setParams] = useSearchParams();
  const tld = params.get('tld_id') ?? '';
  const book = params.get('price_book_id') ?? '';
  const years = [1, 2, 3, 4, 5, 10].find((y) => y === Number(params.get('years'))) ?? 1;
  const op = params.get('operation') ?? '';
  const tldId = tlds.status === 'success' ? (tlds.data.items.find((x) => x.id === tld)?.id ?? tlds.data.items[0]?.id ?? '') : '';
  const books = pricing.status === 'success' ? pricing.data.price_books.filter((b) => b.status === 'ACTIVE') : [];
  // The API's unspecified book is the first by code, which can be a reseller book.
  // Always send the book visible in the selector; STANDARD is the retail base.
  const bookId = books.find((b) => b.id === book)?.id ?? books.find((b) => b.applies_to === 'TIER' && b.tier === 'STANDARD')?.id ?? books[0]?.id ?? '';
  const breakdown = useApi((signal) => (tldId && bookId ? api.getTldPriceBreakdown(tldId, { price_book_id: bookId, years }, { signal }) : Promise.resolve(null)), [tldId, bookId, years]);
  const ops = breakdown.status === 'success' && breakdown.data ? breakdown.data.operations : [];
  const shown = ops.find((o) => o.operation === op) ?? ops[0];
  const usedBook = breakdown.status === 'success' && breakdown.data ? books.find((b) => b.id === breakdown.data?.price_book_id) : undefined;

  return (
    <>
      <PageHeader title={t('quote.title')} subtitle={t('quote.subtitle')} />
      {tlds.status === 'error' ? <DataError error={tlds.error} onRetry={tlds.reload} title={t('quote.loadError')} /> : null}
      {pricing.status === 'error' ? <DataError error={pricing.error} onRetry={pricing.reload} title={t('quote.loadError')} /> : null}
      <FilterBar>
        <Field label={t('quote.tld')} className="min-w-44">
          <select className={selectClass} value={tldId} onChange={(ev) => setParams(setParam(params, 'tld_id', ev.target.value))} disabled={tlds.status !== 'success'}>
            {tlds.status === 'success'
              ? tlds.data.items.map((x) => (
                  <option key={x.id} value={x.id}>
                    .{x.zone}
                  </option>
                ))
              : null}
          </select>
        </Field>
        <Field label={t('quote.book')} className="min-w-52">
          <select className={selectClass} value={bookId} onChange={(ev) => setParams(setParam(params, 'price_book_id', ev.target.value))} disabled={!books.length}>
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} · {b.currency}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('quote.years')} className="min-w-28">
          <select className={selectClass} value={years} onChange={(ev) => setParams(setParam(params, 'years', ev.target.value))}>
            {[1, 2, 3, 4, 5, 10].map((y) => (
              <option key={y} value={y}>
                {t('quote.yearsN', { count: y })}
              </option>
            ))}
          </select>
        </Field>
        {ops.length ? (
          <Field label={t('quote.operation')}>
            <div role="group" aria-label={t('quote.operation')} className="inline-flex h-9 flex-wrap rounded-md border p-0.5">
              {ops.map((o) => (
                <button
                  key={o.operation}
                  type="button"
                  aria-pressed={shown?.operation === o.operation}
                  onClick={() => setParams(setParam(params, 'operation', o.operation))}
                  className={`rounded px-3 text-[13px] font-medium ${shown?.operation === o.operation ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {e('operation', o.operation)}
                </button>
              ))}
            </div>
          </Field>
        ) : null}
      </FilterBar>
      {usedBook ? <p className="mb-4 text-xs text-muted-foreground">{t('quote.usedBook', { book: usedBook.code })}</p> : null}
      {breakdown.status === 'loading' || tlds.status === 'loading' || pricing.status === 'loading' ? <Loading rows={4} /> : null}
      {breakdown.status === 'error' ? <DataError error={breakdown.error} onRetry={breakdown.reload} title={t('quote.loadError')} /> : null}
      {shown ? <OperationQuote op={shown} /> : breakdown.status === 'success' ? <EmptyState title={t('quote.none')} /> : null}
      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Callout tone="info" title={t('quote.feesTitle')}>
          {t('quote.fees')}
        </Callout>
        <Callout tone="neutral" title={t('quote.whereTitle')}>
          {t('quote.where')}{' '}
          <Link to={tldId ? `/registry/tlds/${encodeURIComponent(tldId)}?tab=pricing` : '/products?tab=tlds'} className="font-medium text-primary hover:underline">
            {t('quote.whereLink')}
          </Link>
        </Callout>
      </div>
    </>
  );
}
